import matter from 'gray-matter'
import { marked } from 'marked'

export type BlogLanguage = 'de' | 'en'

export const SUPPORTED_BLOG_LANGUAGES: readonly BlogLanguage[] = ['de', 'en']
export const DEFAULT_BLOG_LANGUAGE: BlogLanguage = 'de'

export interface BlogPostFrontmatter {
  title: string
  slug: string
  date: string
  author: string
  description: string
  tags: string[]
  cover?: string
  draft?: boolean
}

export interface BlogPost extends BlogPostFrontmatter {
  language: BlogLanguage
  contentHtml: string
  contentMarkdown: string
}

export interface BlogPostSummary extends BlogPostFrontmatter {
  language: BlogLanguage
}

const rawModulesDe = import.meta.glob('/src/content/blog/de/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

const rawModulesEn = import.meta.glob('/src/content/blog/en/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

marked.setOptions({
  gfm: true,
  breaks: false,
})

function normalizeDate(value: unknown): string {
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
    if (match) return match[1]
  }
  return '1970-01-01'
}

function parseFile(path: string, raw: string, language: BlogLanguage): BlogPost {
  const { data, content } = matter(raw)
  const filename = path.split('/').pop() ?? ''
  const fallbackSlug = filename.replace(/\.md$/, '').replace(/^\d{4}-\d{2}-\d{2}-/, '')

  const fm: BlogPostFrontmatter = {
    title: String(data.title ?? 'Ohne Titel'),
    slug: String(data.slug ?? fallbackSlug),
    date: normalizeDate(data.date),
    author: String(data.author ?? 'Equna'),
    description: String(data.description ?? ''),
    tags: Array.isArray(data.tags) ? data.tags.map(String) : [],
    cover: data.cover ? String(data.cover) : undefined,
    draft: data.draft === true,
  }

  const contentHtml = marked.parse(content, { async: false }) as string

  return {
    ...fm,
    language,
    contentHtml,
    contentMarkdown: content,
  }
}

const allPosts: BlogPost[] = [
  ...Object.entries(rawModulesDe).map(([path, raw]) => parseFile(path, raw, 'de')),
  ...Object.entries(rawModulesEn).map(([path, raw]) => parseFile(path, raw, 'en')),
]
  .filter((post) => !post.draft)
  .sort((a, b) => b.date.localeCompare(a.date))

const isProduction = import.meta.env.PROD
const publishedPosts = isProduction
  ? allPosts.filter((post) => post.date <= new Date().toISOString().slice(0, 10))
  : allPosts

function normalizeLanguage(input?: string): BlogLanguage {
  if (!input) return DEFAULT_BLOG_LANGUAGE
  const lower = input.toLowerCase()
  for (const lang of SUPPORTED_BLOG_LANGUAGES) {
    if (lower === lang || lower.startsWith(`${lang}-`)) return lang
  }
  return DEFAULT_BLOG_LANGUAGE
}

function postsForLanguage(language: BlogLanguage): BlogPost[] {
  return publishedPosts.filter((post) => post.language === language)
}

export function getAllPosts(language?: string): BlogPostSummary[] {
  const lang = normalizeLanguage(language)
  return postsForLanguage(lang).map(({ contentHtml: _h, contentMarkdown: _m, ...rest }) => rest)
}

export function getPostBySlug(slug: string, language?: string): BlogPost | undefined {
  const lang = normalizeLanguage(language)
  const inLang = postsForLanguage(lang).find((post) => post.slug === slug)
  if (inLang) return inLang
  // Fallback: slug may exist in the other language (e.g. a user shared a DE link
  // while having EN active). Return any match so we never blank-page the user.
  return publishedPosts.find((post) => post.slug === slug)
}

/**
 * All slugs across ALL languages — used for SSG static path generation
 * so every published article gets a pre-rendered HTML file.
 */
export function getAllSlugs(): string[] {
  return Array.from(new Set(publishedPosts.map((post) => post.slug)))
}

export function getAllTags(language?: string): string[] {
  const lang = normalizeLanguage(language)
  const set = new Set<string>()
  postsForLanguage(lang).forEach((post) => post.tags.forEach((tag) => set.add(tag)))
  return Array.from(set).sort((a, b) => a.localeCompare(b, lang))
}

/**
 * All tags across ALL languages — used for SSG static path generation.
 */
export function getAllTagsGlobal(): string[] {
  const set = new Set<string>()
  publishedPosts.forEach((post) => post.tags.forEach((tag) => set.add(tag)))
  return Array.from(set)
}

export function getPostsByTag(tag: string, language?: string): BlogPostSummary[] {
  return getAllPosts(language).filter((post) => post.tags.includes(tag))
}

export function formatBlogDate(iso: string, locale = 'de-DE'): string {
  try {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date)
  } catch {
    return iso
  }
}

export function slugifyTag(tag: string): string {
  return tag
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function findTagBySlug(slugified: string, language?: string): string | undefined {
  const inLang = getAllTags(language).find((tag) => slugifyTag(tag) === slugified)
  if (inLang) return inLang
  return getAllTagsGlobal().find((tag) => slugifyTag(tag) === slugified)
}

export function blogLocaleFor(language: BlogLanguage): string {
  return language === 'en' ? 'en-US' : 'de-DE'
}
